import { DialogoConfirmar, useToast } from '@persona/ui';
import { useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useAtalho } from '../src/atalhos/useAtalho';
import { limiteAbas, lojaAbas, useAbas, type AbaRotina } from './abas';

/**
 * Abas de rotinas abertas (07 §2.7): limite configuravel ate 12, ● quando há alteração não gravada, Alt+W fecha, Alt+←/→ alterna,
 * Alt+Shift+1..9 vai para a aba N (atalhos do navegador; na casca desktop entram os Ctrl).
 */
export function BarraAbas() {
  const { abas, ativa } = useAbas();
  const navegar = useNavigate();
  const [confirmar, setConfirmar] = useState<AbaRotina | null>(null);

  const ir = (a: AbaRotina | null | undefined) => {
    if (a) void navegar({ to: a.caminho });
    else void navegar({ to: '/' });
  };
  const fechar = (a: AbaRotina, forcar = false) => {
    if (a.pendente && !forcar) {
      setConfirmar(a);
      return;
    }
    const proxima = lojaAbas.fechar(a.rotina);
    if (a.rotina === ativa) ir(proxima);
  };
  const deslocar = (d: number) => {
    const i = abas.findIndex((a) => a.rotina === ativa);
    if (abas.length > 1) ir(abas[(i + d + abas.length) % abas.length]);
  };

  useAtalho({ id: 'abas.fechar', teclas: ['Alt+W'], escopo: 'global', descricao: 'Fechar aba', executar: () => { const a = abas.find((x) => x.rotina === ativa); if (a) fechar(a); } });
  useAtalho({ id: 'abas.proxima', teclas: ['Alt+ArrowRight'], escopo: 'global', descricao: 'Próxima aba', executar: () => deslocar(1) });
  useAtalho({ id: 'abas.anterior', teclas: ['Alt+ArrowLeft'], escopo: 'global', descricao: 'Aba anterior', executar: () => deslocar(-1) });
  useAtalho({
    id: 'abas.numero', teclas: ['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((n) => `Alt+Shift+${n}`), escopo: 'global', descricao: 'Ir para a aba N',
    executar: (e) => ir(abas[Number(e.code.replace('Digit', '')) - 1]),
  });

  if (abas.length === 0) return null;
  return (
    <div className="barra-abas" role="tablist" aria-label="Rotinas abertas">
      {abas.map((a) => (
        <div key={a.rotina} role="presentation" className="aba-rotina" data-ativa={a.rotina === ativa || undefined}>
          <button type="button" role="tab" aria-selected={a.rotina === ativa} className="aba-rotina-botao" onClick={() => ir(a)}
            onAuxClick={(e) => e.button === 1 && fechar(a)} title={`${a.rotina} ${a.titulo} — Alt+W fecha`} aria-keyshortcuts="Alt+W">
            <span className="mono texto-3">{a.rotina}</span>
            <span className="aba-rotina-titulo">{a.titulo.length > 24 ? `${a.titulo.slice(0, 24)}…` : a.titulo}</span>
            {a.pendente && <span className="aba-pendente" role="img" aria-label="alteração não gravada" />}
          </button>
          {/* Fechar pelo teclado é Alt+W (anunciado no title da aba); o × é atalho de mouse e fica fora da árvore de acessibilidade do tablist. */}
          <span className="aba-fechar" aria-hidden="true" title={`Fechar ${a.titulo} (Alt+W)`} onClick={() => fechar(a)}>
            <X size={13} />
          </span>
        </div>
      ))}
      <DialogoConfirmar
        aberto={!!confirmar}
        titulo={`Descartar alterações em ${confirmar?.titulo ?? ''}?`}
        texto="O que não foi gravado nesta aba será perdido."
        rotuloConfirmar="Descartar"
        perigo
        aoFechar={() => setConfirmar(null)}
        aoConfirmar={() => { if (confirmar) fechar(confirmar, true); setConfirmar(null); }}
      />
    </div>
  );
}

/** Abre/ativa a aba; ao atingir o limite, sugere fechar a mais antiga livre. */
export function useAbrirAba() {
  const { mostrar } = useToast();
  const navegar = useNavigate();
  return (rotina: string, titulo: string, caminho: string) => {
    if (lojaAbas.abrir(rotina, titulo, caminho)) return true;
    const livre = lojaAbas.maisAntigaLivre();
    mostrar({
      tipo: 'aviso',
      texto: `Feche uma aba para abrir outra (máximo de ${limiteAbas}).`,
      acao: livre ? { rotulo: `Fechar ${livre.rotina} e abrir`, executar: () => { lojaAbas.fechar(livre.rotina); lojaAbas.abrir(rotina, titulo, caminho); void navegar({ to: caminho }); } } : undefined,
    });
    return false;
  };
}
